import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import StickyActionBar from './common/StickyActionBar';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Layout, Plus, Trash2, Loader2, Save, Send, RotateCcw, History, GripVertical,
  Eye, EyeOff, Pencil, ChevronDown, FileText, CheckCircle2, AlertCircle, Copy,
  Image as ImageIcon, Upload, Search, X,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const BLOCK_TYPE_META = {
  hero: { label: 'Hero', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  stats: { label: 'Stats Strip', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  story: { label: 'Story', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  tour: { label: 'Product Tour', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  capabilities: { label: 'Capabilities', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  before_after: { label: 'Before / After', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  pricing: { label: 'Pricing Tiers', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  cta: { label: 'Call to Action', color: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  logo_cloud: { label: 'Logo Cloud', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  feature_grid: { label: 'Feature Grid', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  quote: { label: 'Pull Quote', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  html: { label: 'Custom HTML', color: 'bg-red-100 text-red-700 border-red-200' },
  spacer: { label: 'Spacer', color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const blockLabel = (b) => BLOCK_TYPE_META[b.type]?.label || b.type;
const blockColor = (b) => BLOCK_TYPE_META[b.type]?.color || 'bg-muted text-foreground border';

// ── Smart Field Renderer ─────────────────────────────────────────────
// Recursively renders a JSON-like content object as an editable form.
// Handles: strings (short/long), numbers, booleans, arrays of objects,
// arrays of primitives, and nested objects. Unknown structures fall
// back to a JSON textarea.
function fieldKey(path) { return path.join('.'); }

function setDeep(obj, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  clone[head] = setDeep(clone?.[head], rest, value);
  return clone;
}

function humanize(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function StringField({ label, value, onChange, path }) {
  const isLong = typeof value === 'string' && (value.length > 80 || value.includes('\n') || label.toLowerCase().includes('html') || label.toLowerCase().includes('subhead') || label.toLowerCase().includes('desc'));
  return (
    <div>
      <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
      {isLong ? (
        <Textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 text-xs min-h-[70px] font-mono"
          data-testid={`cms-field-${fieldKey(path)}`}
        />
      ) : (
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 h-8 text-xs"
          data-testid={`cms-field-${fieldKey(path)}`}
        />
      )}
    </div>
  );
}

// Detect image-like field names so we can render an image picker instead of a text input
const IMAGE_KEY_PATTERN = /^(background|image|thumbnail|screenshot|cover|avatar|logo|src|photo|hero_image|bg)$/i;

function isImageField(key) {
  return IMAGE_KEY_PATTERN.test(String(key));
}

// Resolves a stored image reference into a browser-loadable URL.
// Accepts: full https URLs, /assets/... paths, or /api/cms/media/{id}.
function resolveImageSrc(value) {
  if (!value) return '';
  const v = String(value);
  if (/^https?:\/\//i.test(v) || v.startsWith('/')) return v;
  return v;
}

function MediaField({ label, value, onChange, path, token }) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);
  const src = resolveImageSrc(value);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${BACKEND}/api/cms/media`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
      <div className="mt-1 rounded-md border bg-muted/10 p-2 space-y-2" data-testid={`cms-media-field-${fieldKey(path)}`}>
        <div className="flex gap-2">
          <div className="w-24 h-16 rounded border bg-white flex items-center justify-center overflow-hidden shrink-0">
            {src ? (
              <img src={src} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <Input
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              className="h-7 text-[11px] font-mono"
              placeholder="/api/cms/media/... or https://..."
              data-testid={`cms-media-url-${fieldKey(path)}`}
            />
            <div className="flex gap-1.5">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => doUpload(e.target.files?.[0])}
                data-testid={`cms-media-file-input-${fieldKey(path)}`}
              />
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`cms-media-upload-${fieldKey(path)}`}>
                {uploading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Upload className="h-2.5 w-2.5" />}
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => setPickerOpen(true)} data-testid={`cms-media-pick-${fieldKey(path)}`}>
                <ImageIcon className="h-2.5 w-2.5" /> Library
              </Button>
              {value && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-destructive" onClick={() => onChange('')} data-testid={`cms-media-clear-${fieldKey(path)}`}>
                  <X className="h-2.5 w-2.5" /> Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <MediaLibraryDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        token={token}
        onSelect={(url) => { onChange(url); setPickerOpen(false); }}
      />
    </div>
  );
}

function MediaLibraryDialog({ open, onOpenChange, token, onSelect }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const load = React.useCallback(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/cms/media?limit=200${search ? `&search=${encodeURIComponent(search)}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setItems(r.data.items || []))
      .catch(() => toast.error('Failed to load media'))
      .finally(() => setLoading(false));
  }, [open, token, search]);

  React.useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BACKEND}/api/cms/media`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Uploaded');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this media item? Any block using it will break.')) return;
    try {
      await axios.delete(`${BACKEND}/api/cms/media/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="cms-media-library">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" /> Media Library
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Upload images or pick from previously uploaded assets. Everything here can be used as any block image field.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 pb-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by filename, alt text, or tag…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="cms-media-search"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
          <Button size="sm" className="h-8 text-[11px] gap-1" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="cms-media-upload-btn">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {uploading ? 'Uploading…' : 'Upload new'}
          </Button>
        </div>
        <ScrollArea className="flex-1 pr-2">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <ImageIcon className="h-8 w-8 mx-auto opacity-30 mb-2" />
              No media yet. Upload your first image.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {items.map(m => (
                <div key={m.id} className="group relative rounded-md border overflow-hidden bg-muted/20 hover:border-primary/40 transition-colors" data-testid={`cms-media-item-${m.id}`}>
                  <button
                    className="block w-full aspect-square bg-white"
                    onClick={() => onSelect(`${BACKEND}${m.url}`)}
                    title={m.filename}
                  >
                    <img
                      src={`${BACKEND}${m.url}`}
                      alt={m.alt_text || m.filename}
                      className="w-full h-full object-contain"
                    />
                  </button>
                  <div className="p-1.5 border-t bg-white/95">
                    <div className="text-[10px] font-medium truncate">{m.filename}</div>
                    <div className="text-[9px] text-muted-foreground">{Math.round((m.size || 0) / 1024)} KB</div>
                  </div>
                  <button
                    onClick={() => doDelete(m.id)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                    data-testid={`cms-media-delete-${m.id}`}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function NumberField({ label, value, onChange, path }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
      <Input
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 h-8 text-xs"
        data-testid={`cms-field-${fieldKey(path)}`}
      />
    </div>
  );
}

function BoolField({ label, value, onChange, path }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <Label className="text-xs">{label}</Label>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={`cms-field-${fieldKey(path)}`} />
    </div>
  );
}

function ArrayOfObjectsField({ label, value, onChange, path, token }) {
  const items = Array.isArray(value) ? value : [];
  const templateKeys = items.length > 0 ? Object.keys(items[0]) : [];
  const addItem = () => {
    const template = templateKeys.length > 0
      ? Object.fromEntries(templateKeys.map(k => [k, typeof items[0][k] === 'number' ? 0 : (typeof items[0][k] === 'boolean' ? false : '')]))
      : { title: '', sub: '' };
    onChange([...items, template]);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, next) => onChange(items.map((it, idx) => idx === i ? next : it));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label} ({items.length})</Label>
        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={addItem} data-testid={`cms-add-item-${fieldKey(path)}`}>
          <Plus className="h-2.5 w-2.5" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="rounded-md border bg-muted/20 p-2.5 relative">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-muted-foreground">Item {i + 1}</span>
              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeItem(i)} data-testid={`cms-remove-item-${fieldKey(path)}-${i}`}>
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
            {React.createElement(SmartFields, { value: item, onChange: (next) => updateItem(i, next), path: [...path, String(i)], token })}
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-[11px] text-muted-foreground italic py-2">No items yet. Click &quot;Add&quot; to create one.</p>
        )}
      </div>
    </div>
  );
}

function ArrayOfPrimitivesField({ label, value, onChange, path }) {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-medium text-muted-foreground">{label}</Label>
        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => onChange([...items, ''])}>
          <Plus className="h-2.5 w-2.5" /> Add
        </Button>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex gap-1.5">
            <Input
              value={it ?? ''}
              onChange={e => onChange(items.map((v, idx) => idx === i ? e.target.value : v))}
              className="h-7 text-xs"
              data-testid={`cms-field-${fieldKey([...path, String(i)])}`}
            />
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Renders a single value based on its runtime type. Uses a keyed iteration
// instead of mutual recursion between components — this keeps babel's
// static analyzer (visual-edits plugin) from choking on deep JSX cycles.
// The self-referential call uses React.createElement to avoid a JSXOpening
// binding lookup that would infinitely recurse in the visual-edits plugin.
function SmartFields({ value, onChange, path, token }) {
  const obj = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const keys = Object.keys(obj);
  return (
    <div className="space-y-2.5">
      {keys.map(k => {
        const v = obj[k];
        const label = humanize(k);
        const p = [...path, k];
        const setChild = (next) => onChange({ ...obj, [k]: next });

        // Image field detection — route string fields with image-like names to MediaField
        if (isImageField(k) && (v === null || v === undefined || typeof v === 'string')) {
          return <MediaField key={k} label={label} value={v || ''} onChange={setChild} path={p} token={token} />;
        }
        if (v === null || v === undefined) {
          return <StringField key={k} label={label} value="" onChange={setChild} path={p} />;
        }
        if (typeof v === 'boolean') return <BoolField key={k} label={label} value={v} onChange={setChild} path={p} />;
        if (typeof v === 'number') return <NumberField key={k} label={label} value={v} onChange={setChild} path={p} />;
        if (typeof v === 'string') return <StringField key={k} label={label} value={v} onChange={setChild} path={p} />;
        if (Array.isArray(v)) {
          if (v.length === 0 || typeof v[0] === 'object') {
            return <ArrayOfObjectsField key={k} label={label} value={v} onChange={setChild} path={p} token={token} />;
          }
          return <ArrayOfPrimitivesField key={k} label={label} value={v} onChange={setChild} path={p} />;
        }
        if (typeof v === 'object') {
          return (
            <div key={k} className="rounded-md border bg-muted/20 p-2.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide block mb-2">{label}</Label>
              {React.createElement(SmartFields, { value: v, onChange: setChild, path: p, token })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

// Fields per block type that are useful to add if missing (so users can start editing)
const BLOCK_STARTERS = {
  hero: { eyebrow: '', headline_html: '', subhead: '', primary_cta: { label: '', target: '' }, secondary_cta: { label: '', target: '' }, background: '' },
  stats: { items: [{ value: '', label: '' }] },
  story: { kicker: '', headline_html: '', body: '', image: '' },
  tour: { variant: 'tabbed', kicker: '', headline_html: '' },
  capabilities: { kicker: '', headline_html: '', items: [{ title: '', description: '', icon: '' }] },
  before_after: {
    kicker: '', headline_html: '',
    before: { label: 'Before', subtitle: '', items: [{ title: '', sub: '' }] },
    after: { label: 'After', subtitle: '', items: [{ title: '', sub: '' }] },
  },
  pricing: { kicker: '', headline: '', subhead: '' },
  cta: { headline_html: '', subhead: '', button: { label: '', target: '' }, theme: 'dark' },
  logo_cloud: { kicker: '', items: [{ name: '', src: '' }] },
  feature_grid: { kicker: '', headline_html: '', items: [{ title: '', description: '' }] },
  quote: { text: '', attribution: '', role: '' },
  html: { html: '<div></div>' },
  spacer: { height: 40 },
};

// Merge starter fields into existing content so users see all editable fields
function ensureStarterFields(type, content) {
  const starter = BLOCK_STARTERS[type];
  if (!starter) return content || {};
  const c = content && typeof content === 'object' ? content : {};
  const out = { ...c };
  Object.keys(starter).forEach(k => {
    if (out[k] === undefined) out[k] = starter[k];
  });
  return out;
}

// ── Sortable Block Row ────────────────────────────────────────────────
function SortableBlockRow({ block, index, onEdit, onDelete, onToggle, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 'auto' };
  const meta = BLOCK_TYPE_META[block.type];

  const previewSnippet = useMemo(() => {
    const c = block.content || {};
    return c.headline_html || c.headline || c.text || c.eyebrow || c.kicker || (c.items && Array.isArray(c.items) && c.items[0] && (c.items[0].label || c.items[0].title || c.items[0].value)) || '';
  }, [block.content]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-2.5 rounded-lg border bg-card ${isDragging ? 'shadow-lg ring-2 ring-primary/40' : 'hover:border-primary/40'} ${!block.enabled ? 'opacity-50' : ''}`}
      data-testid={`cms-block-row-${block.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        data-testid={`cms-drag-handle-${block.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-[10px] font-mono text-muted-foreground w-6 text-center">{index + 1}</span>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${meta?.color || ''}`}>{blockLabel(block)}</Badge>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate" dangerouslySetInnerHTML={{ __html: previewSnippet ? String(previewSnippet).replace(/<[^>]*>/g, ' ').slice(0, 90) : `<span class="text-muted-foreground italic">Empty ${blockLabel(block).toLowerCase()}</span>` }} />
        <div className="text-[10px] text-muted-foreground">Order {block.order} · {block.enabled ? 'Visible' : 'Hidden'}</div>
      </div>
      <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onToggle(block)} title={block.enabled ? 'Hide' : 'Show'} data-testid={`cms-toggle-${block.id}`}>
          {block.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDuplicate(block)} title="Duplicate" data-testid={`cms-duplicate-${block.id}`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(block)} title="Edit" data-testid={`cms-edit-${block.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(block)} title="Delete" data-testid={`cms-delete-${block.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

// ── Block Editor Dialog ───────────────────────────────────────────────
function BlockEditorDialog({ block, open, onOpenChange, onSave, token }) {
  const [content, setContent] = useState({});
  const [showJson, setShowJson] = useState(false);
  const [rawJson, setRawJson] = useState('');
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (block) {
      const merged = ensureStarterFields(block.type, block.content);
      setContent(merged);
      setRawJson(JSON.stringify(merged, null, 2));
      setJsonError('');
      setShowJson(false);
    }
  }, [block]);

  if (!block) return null;
  const meta = BLOCK_TYPE_META[block.type];

  const applyJson = () => {
    try {
      const parsed = JSON.parse(rawJson);
      setContent(parsed);
      setJsonError('');
      setShowJson(false);
      toast.success('JSON applied');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const save = () => {
    onSave({ ...block, content });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="cms-block-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] ${meta?.color || ''}`}>{blockLabel(block)}</Badge>
            <span>Edit block</span>
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            ID: <code className="text-[10px]">{block.id?.slice(0, 8)}…</code> · Order {block.order}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 pb-2">
          <Button variant={showJson ? 'default' : 'outline'} size="sm" className="h-7 text-[10px] gap-1" onClick={() => { setShowJson(v => !v); setRawJson(JSON.stringify(content, null, 2)); }} data-testid="cms-toggle-json-editor">
            <FileText className="h-3 w-3" /> {showJson ? 'Form editor' : 'JSON editor'}
          </Button>
          <span className="text-[10px] text-muted-foreground">
            {showJson ? 'Advanced: edit raw JSON' : 'Standard: guided form fields'}
          </span>
        </div>
        <ScrollArea className="flex-1 pr-3 -mr-3">
          {showJson ? (
            <div className="space-y-2">
              <Textarea
                value={rawJson}
                onChange={e => setRawJson(e.target.value)}
                className="font-mono text-[11px] min-h-[380px]"
                data-testid="cms-json-textarea"
              />
              {jsonError && <div className="text-[11px] text-destructive">Invalid JSON: {jsonError}</div>}
              <Button size="sm" className="h-7 text-[10px]" onClick={applyJson} data-testid="cms-apply-json">Apply JSON</Button>
            </div>
          ) : (
            <SmartFields value={content} onChange={setContent} path={[]} token={token} />
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cms-cancel-block-edit">Cancel</Button>
          <Button onClick={save} data-testid="cms-save-block-edit">Save block</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Version History Dialog ────────────────────────────────────────────
function VersionHistoryDialog({ open, onOpenChange, slug, headers, onRevert }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);

  useEffect(() => {
    if (!open || !slug) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/cms/pages/${slug}/versions`, { headers })
      .then(r => setVersions(r.data || []))
      .catch(() => toast.error('Failed to load versions'))
      .finally(() => setLoading(false));
  }, [open, slug, headers]);

  const revert = async (versionId) => {
    if (!window.confirm('Revert draft to this version? Your current unsaved draft will be lost.')) return;
    setReverting(versionId);
    try {
      await axios.post(`${BACKEND}/api/cms/pages/${slug}/revert/${versionId}`, {}, { headers });
      toast.success('Reverted to selected version');
      onRevert();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    } finally {
      setReverting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cms-version-history">
        <DialogHeader>
          <DialogTitle>Version history — {slug}</DialogTitle>
          <DialogDescription className="text-[11px]">
            Restore a previous version into your draft. Last {versions.length} versions.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <ScrollArea className="max-h-[400px] pr-2">
            <div className="space-y-1.5">
              {versions.map(v => (
                <div key={v.id} className="flex items-center gap-2 p-2.5 rounded-lg border bg-card" data-testid={`cms-version-${v.id}`}>
                  <Badge variant="outline" className={`text-[10px] ${v.kind === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                    {v.kind === 'published' ? 'Published' : 'Draft'} v{v.version}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium">{v.block_count} block{v.block_count === 1 ? '' : 's'}</div>
                    <div className="text-[10px] text-muted-foreground">{new Date(v.saved_at).toLocaleString()} · {v.saved_by}</div>
                  </div>
                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => revert(v.id)} disabled={reverting === v.id} data-testid={`cms-revert-${v.id}`}>
                    {reverting === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Restore
                  </Button>
                </div>
              ))}
              {versions.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No history yet.</p>}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Page Editor Panel ─────────────────────────────────────────────────
function PageEditor({ token, slug, blockTypes, onDirtyChange, onPagesRefresh }) {
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/cms/pages/${slug}`, { headers })
      .then(r => {
        setPage(r.data);
        const b = (r.data.draft?.blocks || []).slice().sort((x, y) => (x.order || 0) - (y.order || 0));
        setBlocks(b);
        setDirty(false);
      })
      .catch(() => toast.error('Failed to load page'))
      .finally(() => setLoading(false));
  }, [slug, headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const markDirty = () => setDirty(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex(b => b.id === active.id);
    const newIdx = blocks.findIndex(b => b.id === over.id);
    const reordered = arrayMove(blocks, oldIdx, newIdx).map((b, i) => ({ ...b, order: i }));
    setBlocks(reordered);
    markDirty();
  };

  const editBlock = (block) => setEditingBlock(block);
  const saveBlock = (updated) => {
    setBlocks(blocks.map(b => b.id === updated.id ? updated : b));
    markDirty();
  };
  const deleteBlock = (block) => {
    if (!window.confirm(`Delete this ${blockLabel(block).toLowerCase()} block?`)) return;
    setBlocks(blocks.filter(b => b.id !== block.id).map((b, i) => ({ ...b, order: i })));
    markDirty();
  };
  const toggleBlock = (block) => {
    setBlocks(blocks.map(b => b.id === block.id ? { ...b, enabled: !b.enabled } : b));
    markDirty();
  };
  const duplicateBlock = (block) => {
    const copy = {
      ...block,
      id: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      content: JSON.parse(JSON.stringify(block.content || {})),
    };
    const idx = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)].map((b, i) => ({ ...b, order: i }));
    setBlocks(next);
    markDirty();
  };
  const addBlock = (type) => {
    const starter = BLOCK_STARTERS[type] || {};
    const newBlock = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      enabled: true,
      order: blocks.length,
      content: JSON.parse(JSON.stringify(starter)),
    };
    setBlocks([...blocks, newBlock]);
    markDirty();
    setTimeout(() => setEditingBlock(newBlock), 100);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/cms/pages/${slug}/draft`, { blocks }, { headers });
      toast.success('Draft saved');
      setDirty(false);
      load();
      onPagesRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (dirty && !window.confirm('You have unsaved changes. Save draft first?')) return;
    if (dirty) {
      await saveDraft();
    }
    setPublishing(true);
    try {
      await axios.post(`${BACKEND}/api/cms/pages/${slug}/publish`, {}, { headers });
      toast.success('Published! The live site now shows the new version.');
      load();
      onPagesRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const discard = () => {
    if (!dirty) return;
    if (!window.confirm('Discard all unsaved changes?')) return;
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!page) return <div className="p-8 text-center text-sm text-muted-foreground">Page not found.</div>;

  const draftV = page.draft?.version || 0;
  const pubV = page.published?.version || 0;
  const hasUnpub = draftV > pubV;

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b bg-card/60 flex items-center gap-3 shrink-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold truncate" style={{ fontFamily: 'Space Grotesk' }}>{page.name}</h3>
            <Badge variant="outline" className="text-[9px] font-mono">/{slug}</Badge>
            {dirty && <Badge className="text-[9px] bg-amber-500 text-white">Unsaved</Badge>}
            {!dirty && hasUnpub && <Badge className="text-[9px] bg-blue-500 text-white">Draft ahead</Badge>}
            {!dirty && !hasUnpub && pubV > 0 && <Badge className="text-[9px] bg-emerald-500 text-white gap-1"><CheckCircle2 className="h-2.5 w-2.5" />Published</Badge>}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Draft v{draftV} · Published v{pubV || '—'}
            {page.published?.published_at && ` · Last published ${new Date(page.published.published_at).toLocaleString()}`}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => setHistoryOpen(true)} data-testid="cms-history-btn">
            <History className="h-3 w-3" /> History
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => window.open(`/${slug}`, '_blank')} data-testid="cms-preview-btn">
            <Eye className="h-3 w-3" /> View live
          </Button>
          {dirty && (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground" onClick={discard} data-testid="cms-discard-btn">
              Discard
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={saveDraft} disabled={saving || !dirty} data-testid="cms-save-draft-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save draft
          </Button>
          <Button size="sm" className="h-7 text-[10px] gap-1 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600" onClick={publish} disabled={publishing} data-testid="cms-publish-btn">
            {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Publish
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-5 space-y-3 max-w-4xl mx-auto">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" data-testid="cms-block-list">
                {blocks.map((b, i) => (
                  <SortableBlockRow
                    key={b.id}
                    block={b}
                    index={i}
                    onEdit={editBlock}
                    onDelete={deleteBlock}
                    onToggle={toggleBlock}
                    onDuplicate={duplicateBlock}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {blocks.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
              <Layout className="h-8 w-8 mx-auto opacity-30 mb-2" />
              No blocks yet. Add your first block below.
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full h-10 border-dashed gap-2" data-testid="cms-add-block-btn">
                <Plus className="h-4 w-4" /> Add block <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-64 max-h-[300px] overflow-y-auto">
              {blockTypes.map(bt => (
                <DropdownMenuItem key={bt.type} onClick={() => addBlock(bt.type)} className="flex flex-col items-start gap-0.5 py-2" data-testid={`cms-add-type-${bt.type}`}>
                  <span className="text-xs font-semibold">{bt.label}</span>
                  <span className="text-[10px] text-muted-foreground">{bt.desc}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </ScrollArea>

      {/* F3. Sticky Save/Publish bar — visible when unsaved changes exist */}
      <StickyActionBar
        visible={dirty}
        message={<><span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1.5" />You have unsaved changes to <b>{page.name}</b></>}
        align="between"
      >
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={discard} data-testid="cms-sticky-discard">
          Discard
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={saveDraft} disabled={saving} data-testid="cms-sticky-save-draft">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save draft
        </Button>
        <Button size="sm" className="h-8 text-xs gap-1 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600" onClick={publish} disabled={publishing} data-testid="cms-sticky-publish">
          {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Save & Publish
        </Button>
      </StickyActionBar>

      <BlockEditorDialog
        block={editingBlock}
        open={!!editingBlock}
        onOpenChange={(o) => { if (!o) setEditingBlock(null); }}
        onSave={saveBlock}
        token={token}
      />
      <VersionHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        slug={slug}
        headers={headers}
        onRevert={load}
      />
    </div>
  );
}

// ── Page List Sidebar ─────────────────────────────────────────────────
function PageListSidebar({ pages, selected, onSelect, loading }) {
  return (
    <div className="w-64 border-r flex flex-col shrink-0 bg-card/30">
      <div className="p-3 border-b flex items-center justify-between shrink-0">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground tracking-wider">Marketing Pages</span>
        <Badge variant="outline" className="text-[9px] font-mono">{pages.length}</Badge>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="py-6 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {pages.map(p => {
              const unpub = p.has_unpublished_changes;
              const neverPub = !p.published_version;
              return (
                <button
                  key={p.slug}
                  onClick={() => onSelect(p.slug)}
                  className={`w-full text-left p-2.5 rounded-lg transition-colors ${selected === p.slug ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/60 border border-transparent'}`}
                  data-testid={`cms-page-item-${p.slug}`}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-semibold truncate flex-1">{p.name}</span>
                    {unpub && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unpublished changes" />}
                    {neverPub && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Never published" />}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span className="font-mono">/{p.slug}</span>
                    <span>·</span>
                    <span>{p.block_count} blocks</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ── Status Header Tile ────────────────────────────────────────────────
function CMSStatusTile({ pages }) {
  const totalPages = pages.length;
  const unpublished = pages.filter(p => p.has_unpublished_changes).length;
  const neverPublished = pages.filter(p => !p.published_version).length;
  const totalBlocks = pages.reduce((sum, p) => sum + (p.block_count || 0), 0);
  const lastUpdated = pages.reduce((latest, p) => {
    if (!p.last_updated) return latest;
    return !latest || p.last_updated > latest ? p.last_updated : latest;
  }, null);

  return (
    <div className="grid grid-cols-4 gap-2 p-3 border-b bg-gradient-to-r from-violet-50/60 via-fuchsia-50/40 to-transparent dark:from-violet-950/20 dark:via-fuchsia-950/10">
      <div className="rounded-lg border bg-card p-2.5">
        <div className="flex items-center gap-1.5">
          <FileText className="h-3 w-3 text-muted-foreground" />
          <span className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">Pages</span>
        </div>
        <div className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk' }} data-testid="cms-tile-pages">{totalPages}</div>
      </div>
      <div className="rounded-lg border bg-card p-2.5">
        <div className="flex items-center gap-1.5">
          <Layout className="h-3 w-3 text-muted-foreground" />
          <span className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">Total Blocks</span>
        </div>
        <div className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk' }} data-testid="cms-tile-blocks">{totalBlocks}</div>
      </div>
      <div className={`rounded-lg border p-2.5 ${unpublished > 0 ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900' : 'bg-card'}`}>
        <div className="flex items-center gap-1.5">
          <AlertCircle className={`h-3 w-3 ${unpublished > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
          <span className={`text-[10px] uppercase font-semibold tracking-wider ${unpublished > 0 ? 'text-amber-800 dark:text-amber-400' : 'text-muted-foreground'}`}>Unpublished</span>
        </div>
        <div className={`text-lg font-bold mt-0.5 ${unpublished > 0 ? 'text-amber-900 dark:text-amber-300' : ''}`} style={{ fontFamily: 'Space Grotesk' }} data-testid="cms-tile-unpublished">{unpublished}</div>
      </div>
      <div className="rounded-lg border bg-card p-2.5">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
          <span className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">Last Update</span>
        </div>
        <div className="text-[11px] font-medium mt-0.5 truncate" data-testid="cms-tile-last-updated">
          {lastUpdated ? new Date(lastUpdated).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
        </div>
        {neverPublished > 0 && <div className="text-[9px] text-blue-600">{neverPublished} never published</div>}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────
export default function CMSAdmin({ token }) {
  const [pages, setPages] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadPages = useCallback(async () => {
    try {
      const [pRes, btRes] = await Promise.all([
        axios.get(`${BACKEND}/api/cms/pages`, { headers }),
        axios.get(`${BACKEND}/api/cms/block-types`, { headers }),
      ]);
      const pageList = Array.isArray(pRes.data) ? pRes.data : [];
      setPages(pageList);
      setBlockTypes(btRes.data || []);
      if (!selected && pageList.length > 0) {
        const preview = pageList.find(p => p.slug === 'preview') || pageList[0];
        setSelected(preview.slug);
      }
    } catch (e) {
      toast.error('Failed to load CMS. Are you a super admin?');
    } finally {
      setLoading(false);
    }
  }, [headers, selected]);

  useEffect(() => { loadPages(); }, []);

  const handleSelectPage = (slug) => {
    if (dirty && !window.confirm('You have unsaved changes. Switch pages and lose them?')) return;
    setSelected(slug);
  };

  return (
    <div className="h-full flex flex-col" data-testid="cms-admin">
      <CMSStatusTile pages={pages} />
      <div className="flex flex-1 min-h-0">
        <PageListSidebar pages={pages} selected={selected} onSelect={handleSelectPage} loading={loading} />
        <div className="flex-1 min-w-0">
          {selected ? (
            <PageEditor
              key={selected}
              token={token}
              slug={selected}
              blockTypes={blockTypes}
              onDirtyChange={setDirty}
              onPagesRefresh={loadPages}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Layout className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Select a page to edit</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Exports for inline-edit reuse from other pages (e.g. PreviewSite)
export { BlockEditorDialog, BLOCK_STARTERS, ensureStarterFields, BLOCK_TYPE_META };
